import * as React from 'react'

import { Box, Button, Form, Title } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PanesPadForm from 'modules/core/components/PanesPadForm'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import CoffreFortNumeriqueForm from 'modules/coffrefortnumerique/components/Form'
import { Stateable } from 'modules/coffrefortnumerique/lib/enums'
import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import { ValidatorBlock } from 'modules/employees/components/Personal/_payroll/ValidatorBlock'
import PayrollCustomFieldsBlock from 'modules/employees/components/Personal/_payroll/_PayrollCustomFieldsBlock'
import { legalEntityCustomFieldFilter } from 'modules/employees/components/Personal/legalEntityCustomFieldFilter'
import type Employee from 'modules/employees/store/models/Employee'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'
import Codes from 'modules/payroll_integrations/components/Employee/Codes'
import integrationsDescriptor from 'modules/payroll_integrations/components/IntegrationsDescriptor'

import type { IrpfHandle } from './_Irpf'
import Irpf from './_Irpf'

interface Props {
  employee: Employee
  readonly: boolean
  companyId: number
  legalEntity: LegalEntity
  hasCoffreFort: boolean
  contractVersions: ContractVersions
}

const PayrollBlock = (props: Props) => {
  const { employee, readonly, companyId, legalEntity, contractVersions } = props
  const codeRef = React.useRef<Array<Record<string, any>>>([])
  const irpfRef = React.useRef<IrpfHandle>(null)
  const [dirty, setDirty] = React.useState(false)
  const companyInstallations = useLazyRef(() => new CompanyInstallations()).current
  const { loading: loadingIntegrations } = useAsyncData(() => companyInstallations.fetch(), [])

  const showCoffreFortNumeriqueForm = useUnsafeMemo(() => {
    const employeeTerminated = employeeReferenceContract(contractVersions, employee)?.terminated
    const frenchLegalEntity = legalEntity.country === 'fr'

    return props.hasCoffreFort && !employeeTerminated && frenchLegalEntity
  }, [employee, legalEntity])

  useUnsafeEffect(() => {
    codeRef.current = codeRef.current.slice(
      0,
      integrationsDescriptor.enabled(companyInstallations).length
    )
  }, [companyInstallations, loadingIntegrations])

  const onChange = () => {
    const anyDirty = codeRef.current.find((form) => form.isDirty())

    if (!anyDirty && irpfRef.current && !irpfRef.current.isDirty()) setDirty(false)
    else setDirty(true)
  }

  const onSubmit = () => {
    codeRef.current.forEach((form) => {
      form.onSubmit()
    })
    if (irpfRef.current) {
      irpfRef.current.onSubmit()
    }
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: ['Irpf'],
      legal_entity_country: employeeLegalEntity(employee).country,
      layout_version: 'v1',
    })
  }

  const { isPayrollCustomFieldsBlockVisible, isIrpfVisible, hasintegrations } = ValidatorBlock({
    usageGroups: [CustomField.UsageGroups.generalInformation],
    fieldableModelRef: 'company',
    fieldableId: companyId,
    valuableModelRef: 'employee',
    valuableInstance: employee,
    employee: employee,
    installedIntegrations: companyInstallations,
    resourceFieldFilter: legalEntityCustomFieldFilter(legalEntity),
  })

  if (
    !isPayrollCustomFieldsBlockVisible &&
    !isIrpfVisible &&
    !hasintegrations &&
    !showCoffreFortNumeriqueForm
  ) {
    return null
  }

  return (
    <PanesPadForm direction="horizontal" boxed>
      <Title
        variant="500"
        illustration="money-bag"
        title={i18n.t('employee_form.custom_fields.payroll_information.title')}
        description={i18n.t('employee_form.custom_fields.payroll_information.description')}
      />
      <Box>
        {hasintegrations && (
          <Codes
            employee={employee}
            readonly={readonly}
            codeRef={codeRef}
            onChange={onChange}
            installedIntegrations={companyInstallations}
          />
        )}
        {isPayrollCustomFieldsBlockVisible && (
          <PayrollCustomFieldsBlock
            usageGroups={[CustomField.UsageGroups.generalInformation]}
            fieldableModelRef="company"
            fieldableId={companyId}
            valuableModelRef="employee"
            valuableInstance={employee}
            employee={employee}
            readonly={readonly}
            resourceFieldFilter={legalEntityCustomFieldFilter(legalEntity)}
            contractVersions={contractVersions}
          />
        )}
        {isIrpfVisible && (
          <Irpf employee={employee} readonly={readonly} ref={irpfRef} onChange={onChange} />
        )}
        {dirty && (
          <Form.Buttons>
            <Box paddingTop="s24">
              <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
            </Box>
          </Form.Buttons>
        )}
        {showCoffreFortNumeriqueForm && (
          <CoffreFortNumeriqueForm id={employee.id} type={Stateable.Employee} padding={null} />
        )}
      </Box>
    </PanesPadForm>
  )
}

export default observer(PayrollBlock)
